import { Badge, badgeIconVariants, Icon, IconLoader } from "@platform/ui/components";
import { field } from "@platform/ui/styles";
import * as CommandPrimitive from "cmdk";
import * as React from "react";
import { twMerge } from "tailwind-merge";
import { PopoverContent, PopoverTrigger } from "~/shared/components/ds/Popover";

export const ComboboxTrigger = React.forwardRef<
  React.ElementRef<typeof PopoverTrigger>,
  React.ComponentPropsWithoutRef<typeof PopoverTrigger>
>(({ className, children, ...rest }, ref) => {
  return (
    <>
      <PopoverTrigger
        role="combobox"
        ref={ref}
        className={twMerge(
          field(),
          // Focus styles
          "focus-visible:bg-(--field-bg-focus)",
          "focus-visible:border-(--field-border-focus)",
          "focus-within:ring-(--field-ring) focus-visible:ring-2",

          // Invalid styles
          "aria-invalid:border-(--field-border-error)",
          "aria-invalid:ring-2 aria-invalid:ring-(--field-ring-error)",

          // Disabled styles
          "disabled:cursor-not-allowed disabled:text-(--field-text-disabled) disabled:opacity-50",

          // Readonly styles
          "disabled:aria-readonly:text-ds-text-tertiary disabled:aria-readonly:border-(--field-border-disabled) disabled:aria-readonly:bg-(--field-bg-focus) disabled:aria-readonly:opacity-100",

          // Placeholder styles
          "aria-[placeholder]:text-(--field-text-placeholder)",

          // Base styles
          "group/combo group flex h-10 w-full items-center justify-between rounded-md border border-(--field-border) bg-(--field-bg) px-3 py-2 text-left text-sm whitespace-nowrap text-(--field-text) transition-all",
        )}
        {...rest}
      >
        <span className="line-clamp-1 inline-block min-h-0 min-w-0 flex-1 justify-start truncate">{children}</span>
        <Icon
          aria-hidden
          className="group-aria-readonly/combo:group-disabled/combo:text-ds-icon-tertiary h-4 w-4 shrink-0"
          symbol="expand-up-down-line"
        />
      </PopoverTrigger>
    </>
  );
});
ComboboxTrigger.displayName = "ComboboxTrigger";

interface ComboboxContentProps extends React.ComponentProps<typeof PopoverContent> {
  shouldFilter?: boolean;
}
export function ComboboxContent({ children, className, shouldFilter = true, ...props }: ComboboxContentProps) {
  return (
    <PopoverContent
      align="start"
      side="bottom"
      sideOffset={8}
      className={twMerge("w-(--radix-popover-trigger-width) overflow-clip shadow-none", className)}
      {...props}
    >
      <CommandPrimitive.CommandRoot shouldFilter={shouldFilter}>{children}</CommandPrimitive.CommandRoot>
    </PopoverContent>
  );
}

type ComboboxInputProps = React.ComponentPropsWithoutRef<typeof CommandPrimitive.CommandInput> & {
  loading?: boolean;
};
export const ComboboxInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.CommandInput>,
  ComboboxInputProps
>(({ loading, className, ...rest }, ref) => {
  return (
    <div className="border-ds-stroke-primary bg-ds-surface-overlay sticky top-0 z-10 flex flex-row-reverse items-center border-b">
      <CommandPrimitive.CommandInput
        className={twMerge(
          "font-primary text-ds-text-primary placeholder:text-ds-text-placeholder peer flex h-10 w-full border-none bg-transparent px-3 py-1 text-sm outline-hidden transition-colors focus:outline-hidden disabled:cursor-not-allowed",
          className,
        )}
        ref={ref}
        {...rest}
      />
      <span className="text-ds-icon-secondary -mr-3 flex w-auto shrink-0 items-center px-3" aria-hidden>
        {loading ? <IconLoader animate className="size-4" /> : <Icon className="size-4" symbol="search-line" />}
      </span>
    </div>
  );
});
ComboboxInput.displayName = "ComboboxInput";

export const ComboboxList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.CommandList>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.CommandList>
>(({ className, children, ...rest }, ref) => {
  return (
    <CommandPrimitive.CommandList ref={ref} {...rest}>
      <CommandPrimitive.CommandGroup className={twMerge("max-h-[300px] overflow-y-auto p-1", className)}>
        {children}
      </CommandPrimitive.CommandGroup>
    </CommandPrimitive.CommandList>
  );
});
ComboboxList.displayName = "ComboboxList";

interface ComboboxItemProps extends React.ComponentPropsWithoutRef<typeof CommandPrimitive.CommandItem> {
  "data-checked"?: boolean;
}
export const ComboboxItem = React.forwardRef<React.ElementRef<typeof CommandPrimitive.CommandItem>, ComboboxItemProps>(
  ({ className, children, ...rest }, ref) => {
    return (
      <CommandPrimitive.CommandItem
        className={twMerge(
          "text-ds-text-secondary aria-selected:bg-ds-surface-selection aria-selected:text-ds-text-primary data-[checked=true]:text-ds-text-primary group/item relative flex w-full cursor-default items-center justify-between gap-1 overflow-clip rounded-sm p-2 text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50",
          className,
        )}
        ref={ref}
        {...rest}
      >
        {children}
      </CommandPrimitive.CommandItem>
    );
  },
);
ComboboxItem.displayName = "ComboboxItem";

interface ComboboxCheckProps {
  checked?: boolean;
}
export function ComboboxCheck({ checked }: ComboboxCheckProps) {
  return (
    <Icon
      className={twMerge("size-4 shrink-0", checked ? "opacity-100" : "opacity-0")}
      aria-hidden
      symbol="checkbox-check"
    />
  );
}

export const ComboboxEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.CommandEmpty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.CommandEmpty>
>(({ className, ...rest }, ref) => (
  <CommandPrimitive.CommandEmpty
    ref={ref}
    className={twMerge("text-ds-text-primary py-6 text-center text-sm", className)}
    {...rest}
  />
));
ComboboxEmpty.displayName = "ComboboxEmpty";

export const ComboboxSelectionGroup = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<"div">>(
  ({ className, ...rest }, ref) => {
    return <div ref={ref} className={twMerge("flex flex-wrap gap-2", className)} {...rest} />;
  },
);
ComboboxSelectionGroup.displayName = "ComboboxSelectionGroup";

export const ComboboxSelectionBadge = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<"button"> & Pick<React.ComponentProps<typeof Badge>, "colorScheme" | "shape" | "icon">
>(({ className, children, icon, colorScheme = "neutral", shape = "pill", ...rest }, ref) => {
  return (
    <Badge interactive icon={icon} colorScheme={colorScheme} shape={shape} className={className} asChild>
      <button ref={ref} {...rest}>
        {children}
        <Icon className={badgeIconVariants()} symbol="close-line" />
      </button>
    </Badge>
  );
});
ComboboxSelectionBadge.displayName = "ComboboxSelectionBadge";
